import { defaultSettings, type FactStats, type InputMode, type Operation, type Player, type PlayerTotals, type Presentation, type Question, type QuestionResult, type RoundState, type Settings } from './model';
import { MENU_FOODS } from './questions';
import { BADGES, CATS, DECORATIONS, LEGACY_BADGE_DECORATIONS, ROUND_TOKEN_REWARD, type DecorationDefinition } from './rewards';

const INDEX_KEY = 'tables-cafe:index:v1';
const PLAYER_PREFIX = 'tables-cafe:player:v1:';
const MENU_FOOD_IDS = new Set<string>(MENU_FOODS);

export interface PlayerIndex {
  schemaVersion: 1;
  playerIds: string[];
  lastActivePlayerId: string | null;
}

export class StorageProblem extends Error {}

function parse<T>(raw: string | null, label: string): T | null {
  if (!raw) return null;
  try { return JSON.parse(raw) as T; }
  catch { throw new StorageProblem(`${label} could not be read. Your saved text was left untouched.`); }
}

export function loadIndex(): PlayerIndex {
  return parse<PlayerIndex>(localStorage.getItem(INDEX_KEY), 'The player list') ?? { schemaVersion: 1, playerIds: [], lastActivePlayerId: null };
}

export function saveIndex(index: PlayerIndex): void {
  try { localStorage.setItem(INDEX_KEY, JSON.stringify(index)); }
  catch { throw new StorageProblem('This browser could not save the player list. Check storage permissions.'); }
}

export function loadPlayer(id: string): Player | null {
  const saved = parse<unknown>(localStorage.getItem(`${PLAYER_PREFIX}${id}`), 'This player save');
  if (!saved) return null;
  try {
    const player = normalizePlayer(saved);
    if (player.id !== id) throw new Error('The player identity does not match.');
    return player;
  } catch {
    throw new StorageProblem('This player save uses an unsupported format. It was not changed.');
  }
}

export function savePlayer(player: Player): void {
  const updatedAt = new Date().toISOString();
  try { localStorage.setItem(`${PLAYER_PREFIX}${player.id}`, JSON.stringify({ ...player, updatedAt })); player.updatedAt = updatedAt; }
  catch { throw new StorageProblem('Progress could not be saved. Please export a backup before continuing.'); }
}

export function addPlayer(player: Player): void {
  const index = loadIndex();
  if (!index.playerIds.includes(player.id)) index.playerIds.push(player.id);
  index.lastActivePlayerId = player.id;
  savePlayer(player);
  saveIndex(index);
}

export function setActivePlayer(id: string): void {
  const index = loadIndex();
  if (!index.playerIds.includes(id)) throw new StorageProblem('That player is not saved on this device.');
  index.lastActivePlayerId = id;
  saveIndex(index);
}

export function listPlayers(): Player[] {
  const index = loadIndex();
  return index.playerIds.map(loadPlayer).filter((p): p is Player => Boolean(p));
}

export function deletePlayer(id: string): void {
  const index = loadIndex();
  index.playerIds = index.playerIds.filter((saved) => saved !== id);
  if (index.lastActivePlayerId === id) index.lastActivePlayerId = index.playerIds[0] ?? null;
  localStorage.removeItem(`${PLAYER_PREFIX}${id}`);
  saveIndex(index);
}

export function validateImportedPlayer(value: unknown, targetId: string): Player {
  let imported: Player;
  try { imported = normalizePlayer(value); }
  catch { throw new Error('That file is missing required Tables Café save data.'); }
  imported.id = targetId;
  imported.name = imported.name.slice(0, 24);
  imported.updatedAt = new Date().toISOString();
  return imported;
}

function safeWholeNumber(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? Math.max(0, Math.min(Number.MAX_SAFE_INTEGER, Math.floor(value))) : 0;
}

function stringList(value: unknown): string[] {
  return Array.isArray(value) ? [...new Set(value.filter((item): item is string => typeof item === 'string'))] : [];
}

function record(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : null;
}

function normalizeSettings(value: unknown): Settings {
  const source = record(value);
  if (!source) throw new Error('Missing settings.');
  const fallback = defaultSettings('little');
  const validOperations: Operation[] = ['add', 'subtract', 'multiply', 'divide'];
  const operations = stringList(source.operations).filter((operation): operation is Operation => validOperations.includes(operation as Operation));
  if (!operations.length) throw new Error('Missing operations.');
  const numberChoices = (candidate: unknown, fallbackValues: number[]) => {
    const values = Array.isArray(candidate) ? [...new Set(candidate.filter((item): item is number => typeof item === 'number' && Number.isInteger(item) && item >= 1 && item <= 12))] : [];
    return values.length ? values : fallbackValues;
  };
  const volume = typeof source.volume === 'number' && Number.isFinite(source.volume) ? Math.max(0, Math.min(1, source.volume)) : fallback.volume;
  return {
    operations,
    addLimit: [5, 10, 20].includes(Number(source.addLimit)) ? Number(source.addLimit) as 5 | 10 | 20 : fallback.addLimit,
    subtractLimit: [5, 10, 20].includes(Number(source.subtractLimit)) ? Number(source.subtractLimit) as 5 | 10 | 20 : fallback.subtractLimit,
    multiplicationTables: numberChoices(source.multiplicationTables, fallback.multiplicationTables),
    divisionFamilies: numberChoices(source.divisionFamilies, fallback.divisionFamilies),
    factorMax: source.factorMax === 12 ? 12 : 10,
    inputMode: source.inputMode === 'keypad' ? 'keypad' : 'choices',
    puzzles: source.puzzles === true,
    soundPreferred: source.soundPreferred === true,
    volume,
  };
}

function normalizeTotals(value: unknown): PlayerTotals {
  const source = record(value);
  const byOperation = record(source?.byOperation);
  if (!source || !byOperation) throw new Error('Missing totals.');
  const opTotal = (operation: Operation) => safeWholeNumber(byOperation[operation]);
  return {
    independent: safeWholeNumber(source.independent),
    byOperation: { add: opTotal('add'), subtract: opTotal('subtract'), multiply: opTotal('multiply'), divide: opTotal('divide') },
    rounds: safeWholeNumber(source.rounds),
    distinctDoubles: stringList(source.distinctDoubles),
    distinctTens: stringList(source.distinctTens),
    distinctTwos: stringList(source.distinctTwos),
    distinctZeroSubtractions: stringList(source.distinctZeroSubtractions),
    recoveredFacts: stringList(source.recoveredFacts),
  };
}

function normalizeFactStats(value: unknown): Record<string, FactStats> {
  const source = record(value);
  if (!source) throw new Error('Missing fact statistics.');
  return Object.fromEntries(Object.entries(source).flatMap(([key, candidate]) => {
    if (!/^(add|subtract|multiply|divide):\d+:\d+\|(result|missing)\|(choices|keypad)$/.test(key)) return [];
    const stat = record(candidate);
    if (!stat) return [];
    return [[key, {
      firstCorrect: safeWholeNumber(stat.firstCorrect),
      firstMisses: safeWholeNumber(stat.firstMisses),
      helpUses: safeWholeNumber(stat.helpUses),
      independent: safeWholeNumber(stat.independent),
      recent: Array.isArray(stat.recent) ? stat.recent.filter((item): item is boolean => typeof item === 'boolean').slice(-5) : [],
    } satisfies FactStats]];
  }));
}

function limitedString(value: unknown, max = 500): string | null {
  return typeof value === 'string' ? value.slice(0, max) : null;
}

function operation(value: unknown): Operation | null {
  return value === 'add' || value === 'subtract' || value === 'multiply' || value === 'divide' ? value : null;
}

function inputMode(value: unknown, fallback: InputMode): InputMode {
  return value === 'choices' || value === 'keypad' ? value : fallback;
}

function normalizeQuestion(value: unknown): Question | null {
  const source = record(value);
  if (!source) return null;
  const op = operation(source.operation);
  const presentation: Presentation | null = source.presentation === 'result' || source.presentation === 'missing' ? source.presentation : null;
  const id = limitedString(source.id, 120); const equation = limitedString(source.equation); const story = limitedString(source.story); const food = limitedString(source.food, 120); const factKey = limitedString(source.factKey, 160);
  const numbers = [source.a, source.b, source.answer];
  if (!op || !presentation || !id || !equation || !story || !food || !MENU_FOOD_IDS.has(food) || !factKey || numbers.some((number) => typeof number !== 'number' || !Number.isSafeInteger(number) || number < 0 || number > 144)) return null;
  return { id, operation: op, a: source.a as number, b: source.b as number, answer: source.answer as number, presentation, equation, story, food, factKey };
}

function normalizeResult(value: unknown, fallbackMode: InputMode): QuestionResult | null {
  const source = record(value);
  if (!source) return null;
  const op = operation(source.operation);
  const presentation: Presentation | null = source.presentation === 'result' || source.presentation === 'missing' ? source.presentation : null;
  const questionId = limitedString(source.questionId, 120); const factKey = limitedString(source.factKey, 160);
  if (!op || !presentation || !questionId || !factKey) return null;
  return { questionId, factKey, operation: op, presentation, inputMode: inputMode(source.inputMode, fallbackMode), firstCorrect: source.firstCorrect === true, helped: source.helped === true };
}

function normalizeRound(value: unknown, settings: Settings): RoundState | null {
  if (value == null) return null;
  const source = record(value);
  if (!source || typeof source.id !== 'string' || (source.status !== 'active' && source.status !== 'complete')) throw new Error('Invalid round.');
  const currentMode = inputMode(source.currentInputMode, settings.inputMode);
  const validBadgeIds = new Set<string>(BADGES.map((badge) => badge.id));
  const validCatIds = new Set<string>(CATS.map((cat) => cat.id));
  const results = Array.isArray(source.results) ? source.results.map((result) => normalizeResult(result, currentMode)).filter((result): result is QuestionResult => Boolean(result)) : [];
  const operationPlan = Array.isArray(source.operationPlan) ? source.operationPlan.map(operation).filter((item): item is Operation => Boolean(item)) : [];
  const selectedOperations = Array.isArray(source.selectedOperations) ? source.selectedOperations.map(operation).filter((item): item is Operation => Boolean(item)) : [];
  const reviewQueue = Array.isArray(source.reviewQueue) ? source.reviewQueue.flatMap((candidate) => {
    const queued = record(candidate); const question = normalizeQuestion(queued?.question);
    return queued && question ? [{ due: Math.min(10, safeWholeNumber(queued.due)), question }] : [];
  }) : [];
  const tokensEarned = Math.min(ROUND_TOKEN_REWARD, safeWholeNumber(source.tokensEarned));
  return {
    id: source.id.slice(0, 120),
    status: source.status,
    index: Math.min(10, safeWholeNumber(source.index)),
    current: normalizeQuestion(source.current),
    currentInputMode: currentMode,
    currentFirstAttempted: source.currentFirstAttempted === true,
    currentFirstCorrect: source.currentFirstCorrect === true,
    currentHelped: source.currentHelped === true,
    wrongAttempts: Math.min(100, safeWholeNumber(source.wrongAttempts)),
    results,
    operationPlan,
    reviewQueue,
    seenQuestionIds: stringList(source.seenQuestionIds).slice(0, 100),
    selectedOperations: selectedOperations.length ? selectedOperations : [...settings.operations],
    rewardCommitted: source.rewardCommitted === true,
    ...(tokensEarned ? { tokensEarned } : {}),
    newBadges: stringList(source.newBadges).filter((id) => validBadgeIds.has(id)),
    ...(typeof source.newCatId === 'string' && validCatIds.has(source.newCatId) ? { newCatId: source.newCatId } : {}),
  };
}

export function normalizePlayer(value: unknown): Player {
  if (!value || typeof value !== 'object') throw new Error('Not a player save.');
  const source = value as Record<string, unknown>;
  if ((source.schemaVersion !== 1 && source.schemaVersion !== 2) || typeof source.id !== 'string' || typeof source.name !== 'string' || !Array.isArray(source.badges) || !Array.isArray(source.cats)) {
    throw new Error('Unsupported player save.');
  }

  const wasLegacy = source.schemaVersion === 1;
  const imported = structuredClone(source) as unknown as Player;
  imported.schemaVersion = 2;
  imported.name = imported.name.slice(0, 24);
  imported.settings = normalizeSettings(source.settings);
  imported.totals = normalizeTotals(source.totals);
  imported.factStats = normalizeFactStats(source.factStats);
  const validBadgeIds = new Set<string>(BADGES.map((badge) => badge.id));
  const validCatIds = new Set<string>(CATS.map((cat) => cat.id));
  imported.avatar = typeof source.avatar === 'string' && validCatIds.has(source.avatar) ? source.avatar : 'marmalade';
  imported.badges = stringList(source.badges).filter((id) => validBadgeIds.has(id));
  imported.cats = stringList(source.cats).filter((id) => validCatIds.has(id));
  const catalog = new Map<string, DecorationDefinition>(DECORATIONS.map((item) => [item.id, item]));
  imported.cosmetics = stringList(source.cosmetics).filter((id) => catalog.has(id));
  imported.equipped = source.equipped && typeof source.equipped === 'object' ? Object.fromEntries(Object.entries(source.equipped as Record<string, unknown>).filter((entry): entry is [string, string] => typeof entry[1] === 'string' && catalog.get(entry[1])?.slot === entry[0])) : {};

  const owned = new Set(imported.cosmetics);
  Object.values(imported.equipped).forEach((id) => { if (typeof id === 'string') owned.add(id); });
  if (wasLegacy) imported.badges.forEach((badge) => { const item = LEGACY_BADGE_DECORATIONS[badge]; if (item) owned.add(item); });
  imported.cosmetics = [...owned];

  const rounds = imported.totals.rounds;
  imported.tokens = wasLegacy ? Math.min(Number.MAX_SAFE_INTEGER, rounds * ROUND_TOKEN_REWARD) : safeWholeNumber(source.tokens);
  imported.round = normalizeRound(source.round, imported.settings);
  if (imported.round) {
    if (wasLegacy && imported.round.status === 'complete' && imported.round.rewardCommitted) imported.round.tokensEarned = ROUND_TOKEN_REWARD;
  }
  return imported;
}
